'use client'

// Sol menünün altındaki "Prim Hakedişim" kartı — yalnız prim alan aktif operatörde görünür, yalnız kendi
// verisini gösterir: kalan hakediş (hak edilen − ödenen), bu ay hak edilen, yoldaki bekleyen ve son ödeme. Açılışta, sayfa değişiminde (en sık dakikada bir) ve 5 dakikada bir tazelenir.
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { HandCoins } from 'lucide-react'
import type { BonusWidgetData } from '@/app/admin/prim-hakedis/actions'
import { getReadRoute } from '@/lib/read-route-client'
import { formatBonus, monthLabel } from '@/lib/upgrade-bonus'

function shortDay(ymd: string): string {
  const [, m, d] = ymd.split('-')
  return `${d}.${m}`
}

const REFRESH_MS = 5 * 60_000
const MIN_GAP_MS = 60_000

export function BonusWidget({ collapsed }: { collapsed: boolean }) {
  const pathname = usePathname()
  const [data, setData] = useState<BonusWidgetData | null>(null)
  const lastFetch = useRef(0)
  // Yalnız bileşen kaldırılınca sonuç atılır. Sayfa değişiminde atılırsa, açılıştaki
  // hızlı yönlendirmede (giriş → /admin) ilk yanıt kaybolup MIN_GAP yüzünden yeniden
  // istenmiyor, kart 5 dakika boş kalıyordu.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  useEffect(() => {
    const load = async (force = false) => {
      if (document.visibilityState !== 'visible') return
      if (!force && Date.now() - lastFetch.current < MIN_GAP_MS) return
      lastFetch.current = Date.now()
      try {
        const next = await getReadRoute<BonusWidgetData | null>('/api/prim-hakedis/widget')
        if (alive.current) setData(next)
      } catch {
        // geçici hata: son veri kalır
      }
    }
    void load()
    const id = window.setInterval(() => void load(true), REFRESH_MS)
    const onVisible = () => void load()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [pathname])

  if (!data) return null
  const { summary: s, balance: b } = data
  const advance = b.remaining < 0
  const title = `Prim Hakedişim — kalan ${formatBonus(b.remaining)}${advance ? ' (avans)' : ''}, bu ay hak edilen ${formatBonus(s.earnedOpen)}, bekleyen ${formatBonus(s.pending)}`

  if (collapsed) {
    return (
      <Link
        href="/admin/prim-hakedis"
        title={title}
        aria-label={title}
        className="flex h-10 w-full items-center justify-center rounded-md text-emerald-700 hover:bg-accent dark:text-emerald-400"
      >
        <HandCoins className="h-5 w-5" />
      </Link>
    )
  }

  return (
    <Link
      href="/admin/prim-hakedis"
      aria-label={title}
      className="block rounded-lg border bg-card p-3 text-card-foreground shadow-sm transition-colors hover:bg-accent/50"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <HandCoins className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
          Prim Hakedişim
        </span>
        <span className="text-[11px] text-muted-foreground">%{data.rate.toLocaleString('tr-TR')}</span>
      </div>
      <div className="mt-2 space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-muted-foreground">{advance ? 'Kalan (avans)' : 'Kalan hakediş'}</span>
          <span className={`text-base font-bold ${advance ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
            {formatBonus(b.remaining)}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-muted-foreground">Bu ay hak edilen</span>
          <span className="text-sm font-semibold">{formatBonus(s.earnedOpen)}</span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-muted-foreground">Bekleyen (yolda)</span>
          <span className="text-sm font-semibold">{formatBonus(s.pending)}</span>
        </div>
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-wide text-muted-foreground">{monthLabel(s.month.key)}</div>
      <div className="mt-1 grid grid-cols-4 gap-1 text-center">
        {[
          ['Yükseltme', s.month.upgrades],
          ['Teslim', s.month.earned],
          ['Yolda', s.month.inTransit],
          ['İade', s.month.returned],
        ].map(([label, value]) => (
          <div key={label as string} className="rounded bg-muted/60 px-1 py-1">
            <div className="text-sm font-semibold leading-none">{value}</div>
            <div className="mt-0.5 text-[10px] leading-none text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
      {b.paid > 0 && (
        <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2 text-[11px] text-muted-foreground">
          <span>
            Ödenen: <span className="font-medium text-foreground">{formatBonus(b.paid)}</span>
          </span>
          {b.lastPayment && (
            <span>
              Son {shortDay(b.lastPayment.paidOn)} · {formatBonus(b.lastPayment.amount)}
            </span>
          )}
        </div>
      )}
    </Link>
  )
}
